import React from 'react';
import styled from 'styled-components';
import { Portal } from './Portal';
import { FadeUp } from '@nucleus/lib-animation';
import { animated } from 'react-spring';

declare module '@nucleus/react-components' {
  interface ReactComponentsTheme {
    Popover?: {
      zIndex?: number;
    };
  }
}

export interface PopoverOrigin {
  vertical: 'top' | 'center' | 'bottom' | number;
  horizontal: 'left' | 'center' | 'right' | number;
}

export interface PopoverProps {
  /**
   * The element to which the popover is anchored.
   */
  anchorEl?: HTMLElement | null;
  /**
   * The origin point of the anchor element used for positioning the Popover
   */
  anchorOrigin?: PopoverOrigin;
  className?: string;
  children?: React.ReactNode;
  isModal?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  style?: React.CSSProperties;
  /**
   * The origin point of the Container element used for positioning the Popover
   */
  transformOrigin?: PopoverOrigin;
}

/**
 * The Popover component is used to create a container for rendering content that appears as a popover.
 * It can be anchored to a specified element and provides animations for entrance and exit.
 *
 * Inspired by [material-ui/popover](https://mui.com/material-ui/api/popover/)
 *
 * @param props - The props for the Popover component.
 * @returns A JSX element representing the popover.
 */
export const Popover = (props: PopoverProps): JSX.Element | null => {
  const { ref, style } = usePositionedElementProps(props);

  return (
    <FadeUp items={props.isOpen ? [1] : []}>
      {(springValues) => (
        <Portal>
          <ModalRoot>
            <Backdrop interactive={props.isModal} onClick={props.onClose} />
            <Container
              as={animated.div}
              className={props.className}
              style={{ ...props.style, ...style, ...springValues }}
              ref={ref}
            >
              {props.children}
            </Container>
          </ModalRoot>
        </Portal>
      )}
    </FadeUp>
  );
};

const ModalRoot = styled.div`
  position: fixed;
  z-index: ${({ theme }) => theme._reactComponents.Popover?.zIndex ?? 9000};
  inset: 0px;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`;

const Backdrop = styled.div<{ interactive?: boolean }>`
  position: fixed;
  inset: 0px;
  pointer-events: ${({ interactive }) => (interactive ? 'auto' : 'none')};
`;

const Container = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  padding: 6px;
`;

/**
 * Calculates and positions an element according to an anchorElement
 */
const usePositionedElementProps = ({
  anchorEl,
  anchorOrigin = { horizontal: 'center', vertical: 'top' },
  transformOrigin = { horizontal: 'center', vertical: 'bottom' },
  isOpen,
  onClose,
}: PopoverProps) => {
  const [element, setElement] = React.useState<HTMLDivElement | null>(null);
  const [style, setStyle] = React.useState({
    top: '0px',
    left: '0px',
  });

  const calculatePosition = React.useCallback(() => {
    if (anchorEl && element) {
      const anchorRect = anchorEl.getBoundingClientRect();
      const elemRect = {
        width: element.offsetWidth,
        height: element.offsetHeight,
      };

      // On iOS, when the soft keyboard is open, the visual viewport can be
      // panned independently of the layout viewport. `getBoundingClientRect()`
      // reports coordinates in the visual viewport, but `position: fixed`
      // resolves CSS `top`/`left` against the layout viewport. Translate the
      // anchor rect into layout coords and bound checks into the visible area
      // so the popover lands and flips correctly when the keyboard is open.
      // On platforms without a panned visual viewport these offsets are 0 and
      // the math is unchanged.
      const visualViewport = typeof window !== 'undefined' ? window.visualViewport : null;
      const viewportHeight = visualViewport?.height ?? window.innerHeight;
      const viewportWidth = visualViewport?.width ?? window.innerWidth;
      const viewportOffsetTop = visualViewport?.offsetTop ?? 0;
      const viewportOffsetLeft = visualViewport?.offsetLeft ?? 0;
      const viewportTop = viewportOffsetTop;
      const viewportBottom = viewportOffsetTop + viewportHeight;
      const viewportLeft = viewportOffsetLeft;
      const viewportRight = viewportOffsetLeft + viewportWidth;

      const anchorTopLayout = anchorRect.top + viewportOffsetTop;
      const anchorLeftLayout = anchorRect.left + viewportOffsetLeft;

      let top =
        anchorTopLayout +
        anchorRect.height * (anchorOrigin.vertical === 'center' ? 0.5 : anchorOrigin.vertical === 'bottom' ? 1 : 0) -
        elemRect.height * (transformOrigin.vertical === 'center' ? 0.5 : transformOrigin.vertical === 'bottom' ? 1 : 0);

      let left =
        anchorLeftLayout +
        anchorRect.width * (anchorOrigin.horizontal === 'center' ? 0.5 : anchorOrigin.horizontal === 'right' ? 1 : 0) -
        elemRect.width *
          (transformOrigin.horizontal === 'center' ? 0.5 : transformOrigin.horizontal === 'right' ? 1 : 0);

      if (top + elemRect.height > viewportBottom) {
        top = anchorTopLayout - elemRect.height;
      }

      if (top < viewportTop) {
        top = viewportTop;
      }

      if (left < viewportLeft) {
        left = viewportLeft;
      }

      if (left + elemRect.width > viewportRight) {
        left = Math.max(viewportLeft, viewportRight - elemRect.width);
      }

      setStyle({ top: `${top}px`, left: `${left}px` });
    }
  }, [anchorEl, element, anchorOrigin.vertical, anchorOrigin.horizontal, transformOrigin.vertical, transformOrigin.horizontal]);

  React.useEffect(() => {
    if (isOpen !== true) {
      return;
    }
    const removeWindowListeners = addEventListeners(['resize'], calculatePosition, window);
    const visualViewport = typeof window !== 'undefined' ? window.visualViewport : null;
    const removeVisualViewportListeners =
      visualViewport != null
        ? addEventListeners(['resize', 'scroll'], calculatePosition, visualViewport)
        : undefined;
    return () => {
      removeWindowListeners();
      removeVisualViewportListeners?.();
    };
  }, [isOpen, calculatePosition]);

  React.useEffect(() => {
    if (isOpen !== true) {
      return;
    }
    const handler = () => onClose?.();
    return addEventListeners(['scroll', 'wheel', 'resize', 'touchmove'], handler);
  }, [isOpen]);

  React.useEffect(() => {
    if (element) {
      const observer = new ResizeObserver(() => calculatePosition());
      observer.observe(element);
      return () => observer.disconnect();
    }
  }, [element]);

  React.useLayoutEffect(() => {
    calculatePosition();
  }, [calculatePosition]);

  return {
    ref: (node: HTMLDivElement) => setElement(node),
    style: style,
  };
};

function addEventListeners(
  events: Array<string>,
  callback: () => void,
  target: EventTarget = window,
  passive = true
) {
  events.forEach((event) => target.addEventListener(event, callback, { passive: passive }));
  return () => events.forEach((event) => target.removeEventListener(event, callback));
}
